import { MagnifyingGlassIcon, XMarkIcon, Bars3Icon } from '@heroicons/react/24/outline'
import { cx } from 'classix'
import { Link } from 'gatsby'
import { useBreakpoint } from 'gatsby-plugin-breakpoints'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import Container from '@components/common/layout/Container'
import { SearchContextProvider } from '@components/context/SearchContext'
import LocaleSelector from '@components/revamp/LocaleSelector'

import afiULogoHorizontalEn from '@src/assets/svg/afiU-logo-horizontal-en.png'
import afiULogoHorizontalFr from '@src/assets/svg/afiU-logo-horizontal-fr.svg'
import afiULogoFullWhiteEn from '@src/assets/svg/afiU-logo-horizontal-white-en.png'
import afiULogoFullWhiteFr from '@src/assets/svg/afiU-logo-horizontal-white-fr.svg'
import i18n from '@src/config/i18n'
import locales from '@src/config/locales'
import { useOutsideClick } from '@src/hooks/useOutsideClick'

import SearchMenu from './SearchMenu'
import { getMainTabs, MainTab } from './constants/mainTab'
import MobileMenuPopover from './mobile/MobileMenuPopover'
import AboutUsTabMenu from './tabs/AboutUsTabMenu'
import OrganizationsTabMenu from './tabs/OrganizationsTabMenu'
import ProgramsTabMenu from './tabs/ProgramsTabMenu'
import ResourcesTabMenu from './tabs/ResourcesTabMenu'
import TrainingsTabMenu from './tabs/TrainingsTabMenu'

export type NavLink = {
  name: string
  link: string
}

export default function NavBar({
  isSearchMenuOpen = false,
  onSearchMenuClose,
}: {
  isSearchMenuOpen?: boolean
  onSearchMenuClose?: () => void
}) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [selectedTab, setSelectedTab] = useState<MainTab>()
  const { t } = useTranslation('revamp')
  const breakpoints = useBreakpoint()
  const locale = i18n.language

  const logo = breakpoints.lg3
    ? locale === 'fr'
      ? afiULogoHorizontalFr
      : afiULogoHorizontalEn
    : locale === 'fr'
    ? afiULogoFullWhiteFr
    : afiULogoFullWhiteEn

  useEffect(() => {
    setSelectedTab(isSearchMenuOpen ? MainTab.SEARCH : undefined)
  }, [isSearchMenuOpen])

  const closeCurrentTab = useCallback(() => {
    setSelectedTab(undefined)
    onSearchMenuClose?.()
  }, [setSelectedTab, onSearchMenuClose])

  function toggleSearch() {
    if (selectedTab === MainTab.SEARCH) {
      closeCurrentTab()
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' })

      setMobileMenuOpen(false)
      setSelectedTab(MainTab.SEARCH)
    }
  }

  function handleOpenMenu() {
    setMobileMenuOpen(false)
    setSelectedTab(MainTab.SEARCH)
  }

  const navRef = useOutsideClick<HTMLElement>(closeCurrentTab)

  const tabs = getMainTabs(t)

  const blur = useMemo(
    () =>
      selectedTab !== undefined &&
      createPortal(
        <div
          className='fixed inset-0 pointer-events-none'
          style={{ zIndex: 5, backdropFilter: 'blur(5px)' }}
        />,
        document.body
      ),
    [selectedTab]
  )

  function openMobileMenu() {
    setSelectedTab(undefined)
    setMobileMenuOpen(true)
  }

  return (
    <>
      <nav
        ref={navRef}
        className='flex font-avenir text-white bg-revamp-red-600 w-full relative z-10'
        style={{ height: '68px' }}
      >
        <SearchContextProvider>
          <SearchMenu open={selectedTab === MainTab.SEARCH} />
        </SearchContextProvider>
        <TrainingsTabMenu open={selectedTab === MainTab.TRAININGS} />
        <ProgramsTabMenu open={selectedTab === MainTab.PROGRAMS} />
        <OrganizationsTabMenu open={selectedTab === MainTab.ORGANIZATIONS} />
        <AboutUsTabMenu open={selectedTab === MainTab.ABOUT} />
        <ResourcesTabMenu open={selectedTab === MainTab.RESOURCES} />
        <Container className='flex items-center h-full w-full lg3:hidden z-10'>
          <Link className='w-full' to={locales[locale].homePath}>
            <img className='w-60 h-auto' width={240} src={logo} alt='logo' />
          </Link>
          <div
            role='button'
            className='w-full items-center justify-end gap-5 flex rounded-md text-white'
          >
            <span className='sr-only'>{t('openMainMenu')}</span>
            <button
              className={cx('ml-auto', selectedTab !== undefined ? 'visible' : 'invisible')}
              onClick={closeCurrentTab}
            >
              <XMarkIcon className='rounded-full border border-white text-white h-5 w-5' />
            </button>
            <button
              className={cx(
                selectedTab === MainTab.SEARCH ? 'hidden' : 'flex',
                'h-12 items-center justify-center py-4 opacity-80'
              )}
              onClick={toggleSearch}
            >
              <MagnifyingGlassIcon className='text-white h-6 w-6' />
            </button>

            {selectedTab !== MainTab.SEARCH && (
              <Bars3Icon onClick={openMobileMenu} className='h-8 w-8' aria-hidden='true' />
            )}
          </div>
        </Container>
        <Container className='hidden h-full lg3:flex items-center w-full z-10'>
          <Link to={locales[locale].homePath}>
            <img className='w-48 h-auto xl3:w-60' width={240} src={logo} />
          </Link>
          <div className='ml-auto h-full flex items-center font-bold text-base leading-mid gap-6 xl3:gap-8'>
            {tabs.map((tab) => (
              <div key={`tab_header_${tab.name}`} className='relative h-full flex items-center'>
                <button className='font-bold' onMouseEnter={() => setSelectedTab(tab.key)}>
                  {tab.name}
                </button>
                <span
                  className={cx(
                    'absolute top-0 left-0 w-full h-1 bg-white transition-opacity',
                    selectedTab === tab.key ? 'opacity-100' : 'opacity-0'
                  )}
                />
              </div>
            ))}
          </div>
          <div className='ml-8 xl3:ml-24 flex items-center gap-6 justify-center'>
            <LocaleSelector className='hover:underline' />
            <button onMouseEnter={handleOpenMenu} onClick={handleOpenMenu}>
              <MagnifyingGlassIcon className='text-white h-6 w-6' />
            </button>
            <button
              className={cx(selectedTab !== undefined ? 'visible' : 'invisible')}
              onClick={closeCurrentTab}
            >
              <XMarkIcon className='rounded-full border border-white text-white h-5 w-5' />
            </button>
          </div>
        </Container>
      </nav>
      <MobileMenuPopover open={mobileMenuOpen} close={() => setMobileMenuOpen(false)} />
      {blur}
    </>
  )
}
